import React, { useEffect, useState } from 'react';
import Modal from 'components/Modal';
import { Button, Card, Col, Form, Input, Row, Select, Space } from 'antd';
import { gql } from '@apollo/client';
import { useMutation, useQuery } from 'hooks/apollo';
import useCustomer from 'hooks/useCustomer';
import SurveyCategorySelect from 'components/SurveyCategorySelect';
import { getLocaleFromUrl } from 'shared/utils/urlToLocale';
import useGlobalContext from 'hooks/useGlobalContext';
import useUser from 'hooks/useUser';
import { DataSourceType } from 'shared/services/prisma/types';
import DataSourceTypeLabel from 'components/DataSourceTypeLabel';
import LanguageSelect from 'components/LanguageSelect';
import websiteImage from './website.jpg';
import otherImage from './other.jpg';

const USER_QUERY = gql`
  query Account_Users($id: ID!) {
    customer(id: $id) {
      id
      customerAccess {
        id
        user {
          id
          email
        }
      }
    }
  }
`;

const CREATE_DATA_SOURCE = gql`
  mutation($customerId: ID!, $input: DataSourceInput!) {
    createDataSource(customerId: $customerId, input: $input) {
      id
    }
  }
`;

const CreateDataSourceForm = ({ type, form, onFinish }) => {
  const { isSuperUser } = useUser();
  const { customerId } = useCustomer();
  const { data, loading } = useQuery(USER_QUERY, { variables: { id: customerId, skip: !customerId || !isSuperUser } });

  const onValuesChange = (changedValues) => {
    if (changedValues.url) {
      const language = getLocaleFromUrl(changedValues.url);
      if (language) {
        form.setFieldsValue({
          language,
        });
      }
    }
  };

  return (
    <Form form={form} layout="vertical" onFinish={onFinish} onValuesChange={onValuesChange}>
      {type === DataSourceType.Website && (
        <>
          <Form.Item name={['website', 'url']} label="Enter the address of your website" rules={[{ required: true, type: 'url', message: 'Enter a valid url' }]}>
            <Input placeholder="https://www.mysite.com" autoComplete="url" />
          </Form.Item>
          <Form.Item name={['website', 'language']} label="Primary language" help="You will be able to add more languages later.">
            <LanguageSelect allowOther />
          </Form.Item>
          <Form.Item name={['website', 'categoryIds']} label="Industry" help="This will let you compare your result to other companies in the same industry.">
            <SurveyCategorySelect mode="multiple" />
          </Form.Item>
        </>
      )}
      {type === DataSourceType.Cx && (
      <Form.Item name="title" label="Enter the name of your data source" rules={[{ required: true, message: 'Enter a name' }]}>
        <Input placeholder="My data source" />
      </Form.Item>
      )}
      {isSuperUser && (
        <Form.Item name="ownerId" label="Owner" rules={[{ required: true, message: 'Select an owner, or invite the user before creating the data source.' }]}>
          <Select options={data?.customer.customerAccess.map(ca => ({ value: ca.user.id, label: ca.user.email }))} loading={loading} />
        </Form.Item>
      )}
    </Form>
  );
};

const CreateDataSource = ({ open, onCancel, onDone }) => {
  const { customer } = useCustomer();
  const { message } = useGlobalContext();
  const [step, setStep] = useState(1);
  const [type, setType] = useState();
  const [form] = Form.useForm();
  const [createDataSource, { loading: creating }] = useMutation(CREATE_DATA_SOURCE);

  useEffect(() => {
    setStep(1);
  }, [open]);

  const goBack = () => {
    setStep(step - 1);
  };

  const onSelectType = (t) => {
    setType(t);
    setStep(2);
  };

  const onDataSourceFormFinish = async (values) => {
    try {
      const { data } = await createDataSource({
        variables: {
          customerId: customer.id,
          input: {
            type,
            ...values,
          },
        },
      });
      onDone(data.createDataSource.id);
    } catch (e) {
      message.error(e.message);
    }
  };

  return (
    <Modal
      title="New data source"
      centered
      open={open}
      width={600}
      footer={step > 1 ? (
        <Space>
          {step === 3 && <Button onClick={goBack}>Back</Button>}
          {step === 2 && (
          <Button type="primary" onClick={form.submit} loading={creating}>
            Create
          </Button>
          )}
        </Space>
      ) : null}
      onCancel={onCancel}
    >
      {step === 1 && (
        <Row gutter={16}>
          <Col span={12}>
            <Card title={<DataSourceTypeLabel type={DataSourceType.Website} />} hoverable onClick={() => onSelectType(DataSourceType.Website)} cover={<img src={websiteImage} alt="Website illustration" />} style={{ height: '100%' }}>
              Collect analytical and survey data from your website visitors.
            </Card>
          </Col>
          {/* <Col span={8}>
          <Card title="App" extra={<MobileOutlined />} hoverable cover={<img src={mailImage} alt="Mobile application illustration" />} style={{ height: '100%' }}>
            Collect survey responses from your mobile app users.
          </Card>
        </Col> */}
          <Col span={12}>
            <Card title={<DataSourceTypeLabel type={DataSourceType.Cx} />} hoverable onClick={() => onSelectType(DataSourceType.Cx)} cover={<img src={otherImage} alt="Customer experience illustration" />} style={{ height: '100%' }}>
              Collect data from known customers through email or other channels.
            </Card>
          </Col>
        </Row>
      )}
      {step === 2 && (
        <CreateDataSourceForm type={type} form={form} onFinish={onDataSourceFormFinish} />
      )}
    </Modal>
  );
};

export default CreateDataSource;
